import { Component, Project } from "projen";
import { GitHub } from "projen/lib/github";
import { JobStep } from "projen/lib/github/workflows-model";
import { VERSION } from "../versions";

/**
 * Prefix matched against {@link JobStep.uses} to identify
 * `actions/setup-node` steps.
 */
const SETUP_NODE_PREFIX = "actions/setup-node@";

/**
 * Component property names that, when present, hold an array of
 * {@link JobStep} objects we want to scan for `actions/setup-node`
 * `node-version` inputs.
 *
 * Mirrors {@link pinPnpmActionSetup}'s traversal: projen's
 * {@code BuildWorkflow} stores them on a private
 * {@code preBuildSteps} field; {@code Release} stores them on
 * {@code preBuildSteps} (public-ish); both feed steps into the lazy
 * step closures rendered at synth time, so simply walking
 * {@code workflow.jobs[*].steps} during {@code preSynthesize} misses
 * them. This array enumerates the well-known fields we reach into
 * directly.
 */
const STEP_ARRAY_FIELDS = ["preBuildSteps", "postBuildSteps"] as const;

/**
 * Component property names that, when present, hold a string value
 * carrying the Node version a release workflow renders into a
 * `tools.node.version` block (and therefore into a
 * `with["node-version"]` input on the auto-generated
 * `actions/setup-node` step). Projen's {@code Publisher} stores its
 * version under {@code workflowNodeVersion} and defaults it to
 * `"lts/*"` when no value is supplied.
 */
const NODE_VERSION_FIELDS = ["workflowNodeVersion"] as const;

/**
 * Rewrites every Node-version source reachable from a project's
 * GitHub component (and its sub-projects) to the version pinned in
 * {@link VERSION.NODE_WORKFLOWS}.
 *
 * Projen hardcodes `node-version: "lts/*"` in its release workflow
 * generator (via {@code Publisher.workflowNodeVersion}); configulator-
 * owned workflows already pass through {@link VERSION.NODE_WORKFLOWS}.
 * Without this patch, the two sources disagree (release workflows
 * install whatever Node lts/* resolves to; build / upgrade workflows
 * install Node 24). This helper post-patches every Node-version
 * source so a single source of truth — the centralised
 * {@link VERSION.NODE_WORKFLOWS} constant — drives every workflow
 * this repo and its consumers generate.
 *
 * Three passes run in sequence:
 *
 * 1. **Component Node-version fields.** Walks every {@link Component}
 *    on the project (and on its sub-projects) and rewrites the
 *    well-known fields enumerated in {@link NODE_VERSION_FIELDS} —
 *    notably {@code Publisher.workflowNodeVersion}, which projen
 *    renders into {@code tools.node.version} on the publish job
 *    (and ultimately into the synthesized
 *    `actions/setup-node` step).
 * 2. **Component step arrays.** Walks every {@link Component} on the
 *    project (and on its sub-projects) for the well-known step-array
 *    fields enumerated in {@link STEP_ARRAY_FIELDS}. Projen's
 *    BuildWorkflow and Release components hold their pre/post-build
 *    steps in these arrays before the workflow's lazy step closures
 *    resolve them.
 * 3. **Direct workflow jobs.** Walks every job on every
 *    `GithubWorkflow`. For each job:
 *    - If `tools.node.version` is set (the lazy `setupTools` path
 *      that synthesises the setup-node step at render time),
 *      rewrite it.
 *    - If `steps` is already an array (i.e. not a lazy closure),
 *      patch any `actions/setup-node` step in place.
 *
 * The step-level patch only mutates `with["node-version"]`. Other
 * fields on `with` (`cache`, `cache-dependency-path`,
 * `registry-url`, `package-manager-cache`, etc.) are preserved
 * untouched. The step's `uses` field is also left alone — this
 * helper pins the input, not the action version pin.
 *
 * @param project - The project whose GitHub workflows should be patched.
 */
export function pinSetupNodeVersion(project: Project): void {
  const pinned = VERSION.NODE_WORKFLOWS;
  // Pass 1 + 2: walk components on this project and every sub-project.
  patchComponentNodeVersionFields(project, pinned);
  patchComponentStepArrays(project, pinned);
  for (const sub of project.subprojects) {
    patchComponentNodeVersionFields(sub, pinned);
    patchComponentStepArrays(sub, pinned);
  }
  // Pass 3: walk eagerly-populated workflow job steps and the
  // `tools.node.version` block on every job. Sub-project release
  // workflows attach to the root GitHub via `GitHub.of(project.root)`,
  // so a single pass covers them.
  const github = GitHub.of(project);
  if (!github) {
    return;
  }
  for (const workflow of github.workflows) {
    for (const job of Object.values(workflow.jobs)) {
      patchJobToolsNode(job, pinned);
      patchStepArray(getEagerSteps(job), pinned);
    }
  }
}

/**
 * Walks {@link NODE_VERSION_FIELDS} on every {@link Component} of the
 * given project and rewrites the field's string value to the pinned
 * version. Used to retarget {@code Publisher.workflowNodeVersion}
 * (and any future component that exposes a similar field).
 */
function patchComponentNodeVersionFields(
  project: Project,
  pinned: string,
): void {
  for (const component of project.components) {
    for (const field of NODE_VERSION_FIELDS) {
      const record = component as unknown as Record<string, unknown>;
      const value = record[field];
      if (typeof value === "string") {
        record[field] = pinned;
      }
    }
  }
}

/**
 * Walks {@link STEP_ARRAY_FIELDS} on every {@link Component} of the
 * given project and rewrites `actions/setup-node` `node-version`
 * inputs in any step arrays that are eagerly populated.
 */
function patchComponentStepArrays(project: Project, pinned: string): void {
  for (const component of project.components) {
    for (const field of STEP_ARRAY_FIELDS) {
      const steps = readStepArray(component, field);
      if (steps !== undefined) {
        patchStepArray(steps, pinned);
      }
    }
  }
}

/**
 * Reads a {@code JobStep[]}-shaped property off a component, casting
 * past projen's private/readonly declarations. Returns `undefined`
 * when the property is missing or not an array.
 */
function readStepArray(
  component: Component,
  field: string,
): Array<MutableStep> | undefined {
  const value = (component as unknown as Record<string, unknown>)[field];
  return Array.isArray(value) ? (value as Array<MutableStep>) : undefined;
}

/**
 * Reads {@code job.steps} only when it is already an array (i.e. not
 * a lazy closure). Workflows that build their steps via a closure
 * are reached via the component-level pass instead.
 */
function getEagerSteps(job: unknown): Array<MutableStep> | undefined {
  if (typeof job !== "object" || job === null) {
    return undefined;
  }
  const steps = (job as { steps?: unknown }).steps;
  return Array.isArray(steps) ? (steps as Array<MutableStep>) : undefined;
}

/**
 * Rewrites {@code job.tools.node.version} when it is set. Projen's
 * `setupTools` helper synthesises an `actions/setup-node` step at
 * workflow render time from this field — patching the source string
 * here is the only path that reaches release-workflow setup-node
 * steps, since those steps never appear in a `steps[]` array before
 * render.
 */
function patchJobToolsNode(job: unknown, pinned: string): void {
  if (typeof job !== "object" || job === null) {
    return;
  }
  const tools = (job as { tools?: unknown }).tools;
  if (typeof tools !== "object" || tools === null) {
    return;
  }
  const node = (tools as { node?: unknown }).node;
  if (typeof node !== "object" || node === null) {
    return;
  }
  if (typeof (node as { version?: unknown }).version === "string") {
    (node as { version: string }).version = pinned;
  }
}

/**
 * Mutates a step array in place: rewrites every step whose
 * {@code uses} field starts with the `actions/setup-node` prefix to
 * carry `with["node-version"] = pinned`. Other fields on `with` are
 * preserved; if the step has no `with` block, one is created.
 */
function patchStepArray(
  steps: Array<MutableStep> | undefined,
  pinned: string,
): void {
  if (!steps) {
    return;
  }
  for (const step of steps) {
    if (
      typeof step.uses === "string" &&
      step.uses.startsWith(SETUP_NODE_PREFIX)
    ) {
      if (!step.with || typeof step.with !== "object") {
        step.with = {};
      }
      step.with["node-version"] = pinned;
    }
  }
}

/**
 * Internal mutable view of a {@link JobStep} so we can rewrite the
 * `with` map past projen's `readonly` declarations.
 */
interface MutableStep {
  uses?: string;
  with?: Record<string, unknown>;
}
